import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Space
} from 'antd';
import {
  AddressType,
  CardHolderType,
  CompanyIdentification,
  Gender,
  IdentificationTypes,
} from 'interfaces/card-holder';
import React, { useState } from 'react';
import { Countries } from '../../../constants';
import ModalIdentification from '../../../components/Identifications/modal-identification';
import ModalAddresses, { AddressesFormInputs } from '../../../components/Addresses/modal-addresses';
import Addresses from '../../../components/Addresses/addresses';
import ModalPhones from '../../../components/Phones/modal-phones';
import Phones, { PhoneFormInputs } from '../../../components/Phones/phones';
import { IdentificationsFormInputs } from 'components/Identifications/types/IdentificationsTypes';
import { SecondStepFormInputs, SecondStepProps } from './types/secondStepTypes';
import Identifications from 'components/Identifications/identifications';

const validateLegalAddress = (addresses: AddressesFormInputs[]) : boolean => {
    return addresses.some((obj) => obj['type'] == AddressType.Legal)
}

const validateCuil = (identifications: IdentificationsFormInputs[], nationality: string, isPerson: boolean) : boolean => {
  return  (  nationality != "ARG" || (isPerson && identifications.some((obj) => obj['type'] == IdentificationTypes.CUIL)))
}

const validateCuit = (identifications: IdentificationsFormInputs[], isPerson: boolean) : boolean => {
  return  (!isPerson && identifications.some((obj) => obj['type'] == IdentificationTypes.CUIT))
}

const SubmitButton = ({
  form,
  addresses,
  identifications,
  isPerson
}: {
  form: FormInstance;
  addresses: AddressesFormInputs[];
  identifications: IdentificationsFormInputs[];
  isPerson: boolean
}) => {
  const [submittable, setSubmittable] = React.useState(false);

  const values = Form.useWatch([], form);

  React.useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        if (addresses.length > 0 && identifications.length > 0 && validateLegalAddress(addresses) && (validateCuil(identifications, form.getFieldValue("nationality"), isPerson) || validateCuit(identifications, isPerson))) {
          setSubmittable(true);
        } else {
          setSubmittable(false);
        }
      },
      () => {
        setSubmittable(false);
      }
    );
  }, [values, identifications, addresses]);

  return (
    <Button type="primary" htmlType="submit" disabled={!submittable}>
      Siguiente
    </Button>
  );
};

const SecondStep: React.FC<SecondStepProps> = ({
  goToPrevious,
  goToNext,
  firstStepData,
  setSecondStepData,
  secondStepData,
}) => {
  const [identificationsOpen, setIdentificationsOpen] = useState(false);
  const [identifications, setIdentifications] = useState<IdentificationsFormInputs[]>(
    secondStepData?.identifications ?? []
  );

  const [addressesOpen, setAddressesOpen] = useState(false);
  const [addresses, setAddresses] = useState<AddressesFormInputs[]>(secondStepData?.addresses ?? []);

  const [phonesOpen, setPhonesOpen] = useState(false);
  const [phones, setPhones] = useState<PhoneFormInputs[]>(secondStepData?.phones ?? []);

  const [form] = Form.useForm<SecondStepFormInputs>();

  const isPerson = firstStepData?.cardHolderType === CardHolderType.Person;

  const onSubmit = (data: SecondStepFormInputs) => {
    setSecondStepData({ ...data, identifications, addresses, phones });
    goToNext();
  };

  const onCreateIdentification = (values: IdentificationsFormInputs) => {
    setIdentifications((prev) => {
      return [...prev, values];
    });

    setIdentificationsOpen(false);
  };

  const onCreateAddress = (values: AddressesFormInputs) => {
    setAddresses((prev) => {
      const newAddress = [...prev, values];
      return newAddress;
    });

    setAddressesOpen(false);
  };

  const onCreatePhone = (values: PhoneFormInputs) => {
    setPhones((prev) => {
      const newPhone = [...prev, values];
      return newPhone;
    });

    setPhonesOpen(false);
  };

  return (
    <>
      <Form<SecondStepFormInputs>
        layout="vertical"
        onFinish={onSubmit}
        initialValues={secondStepData || undefined}
        form={form}
        style={{ minWidth: '420px', maxWidth: '100%', margin: 'auto', marginBottom:'0.5em'}}
      >
        <Row gutter={24}>
          <Col span={8}>
            {isPerson ? (
              <Form.Item<SecondStepFormInputs>
                label="Nombre"
                name="firstName"
                rules={[{ required: true, message: 'Por favor completar con nombre.' }]}
              >
                <Input />
              </Form.Item>
            ) : (
              <Form.Item<SecondStepFormInputs>
                label="Nombre legal"
                name="legalName"
                rules={[{ required: true, message: 'Por favor completar con nombre.' }]}
              >
                <Input />
              </Form.Item>
            )}
          </Col>

          <Col span={8}>
            {isPerson ? (
              <Form.Item<SecondStepFormInputs>
                label="Apellido"
                name="lastName"
                rules={[{ required: true, message: 'Por favor completar con apellido.' }]}
              >
                <Input />
              </Form.Item>
            ) : (
              <Form.Item<SecondStepFormInputs>
                label="Nombre de fantasia"
                name="tradeName"
                rules={[{ required: true, message: 'Por favor completar con apellido.' }]}
              >
                <Input />
              </Form.Item>
            )}
          </Col>

          <Col span={8}>
            {isPerson ? (
              <Form.Item<SecondStepFormInputs>
                label="Genero"
                name="gender"
                rules={[{ required: true, message: 'Por favor selecciona una opcion.' }]}
              >
                <Select
                  placeholder="Seleccionar un genero."
                  options={[
                    { label: 'Masculino', value: Gender.Male },
                    { label: 'Femenino', value: Gender.Female },
                    { label: 'Sin Genero', value: Gender.Agender },
                  ]}
                />
              </Form.Item>
            ) : (
              <Form.Item<SecondStepFormInputs>
                label="Tipo de Identificacion"
                name="identificationType"
                rules={[{ required: true, message: 'Por favor selecciona una opcion.' }]}
              >
                <Select
                  placeholder="Seleccionar identificacion."
                  options={Object.values(CompanyIdentification).map((idType) => ({ label: idType, value: idType }))}
                />
              </Form.Item>
            )}
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={8}>
            {isPerson ? (
              <Form.Item<SecondStepFormInputs>
                label="Fecha de Nacimiento"
                name="birthDate"
                rules={[{ required: true, message: 'Por favor selecciona una fecha valida.' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            ) : (
              <Form.Item<SecondStepFormInputs>
                label="Fecha de Inscripcion"
                name="inscriptionDate"
                rules={[{ required: true, message: 'Por favor selecciona una fecha valida.' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            )}
          </Col>
          <Col span={8}>
            {' '}
            <Form.Item<SecondStepFormInputs>
              label="Nacionalidad"
              name="nationality"
              rules={[{ required: true, message: 'Por favor selecciona una opcion.' }]}
            >
              <Select
                placeholder="Seleccionar nacionalidad"
                showSearch
                options={Object.entries(Countries).map(([code, name]) => ({ label: name, value: code }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<SecondStepFormInputs>
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Por favor selecciona una opcion.' },
                { type: 'email', message: 'Por favor ingresar un email valido.', validateTrigger: 'onBlur' },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item<SecondStepFormInputs>
              label="ID Externo"
              name="externalId"
              rules={[{ required: true, message: 'Por favor completar.' }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Identifications
          identifications={ identifications }
          setIdentifications={ setIdentifications }
        />

        {identifications.length > 0 && !validateCuil(identifications, form.getFieldValue("nationality"), isPerson) && (
            <Col span={24} style={{marginTop:'0.5em'}}>
                <Alert
                  message="Es necesario una identificación del tipo CUIL."
                  type="warning"
                  showIcon
                />
            </Col>
        )}

        {identifications.length > 0 && !validateCuit(identifications, isPerson) && (
            <Col span={24} style={{marginTop:'0.5em'}}>
                <Alert
                  message="Es necesario una identificación del tipo CUIT."
                  type="warning"
                  showIcon
                />
            </Col>
        )}

        <Addresses
          addresses={ addresses }
          setAddresses={ setAddresses }
        />

        {(addresses.length >0 && !validateLegalAddress(addresses)) && (
            <Col span={24} style={{marginTop:'0.5em'}}>
                <Alert
                  message="Es necesario una direccion del tipo LEGAL."
                  type="warning"
                  showIcon
                />
            </Col>
        )}

        <Phones
          phones={phones}
          setPhones={ setPhones }
        />
        <Form.Item style={{ textAlign: 'center' }}>
          <Space>
            <Button onClick={goToPrevious}>Anterior</Button>
            <SubmitButton form={form} addresses={addresses} identifications={identifications} isPerson={isPerson}/>
          </Space>
        </Form.Item>
      </Form>
      <ModalIdentification
        open={identificationsOpen}
        onCreate={onCreateIdentification}
        onCancel={() => setIdentificationsOpen(false)}
      />
      <ModalAddresses
        open={addressesOpen}
        onCreate={onCreateAddress}
        onCancel={() => setAddressesOpen(false)}
      />
      <ModalPhones open={phonesOpen} onCreate={onCreatePhone} onCancel={() => setPhonesOpen(false)} />
    </>
  );
};

export default SecondStep;
